import { ParsedUrlQuery } from 'querystring';

import { ColumnSort, PaginationState, RowData, SortingState } from '@tanstack/react-table';

import { ColumnWidth, FilterConditionGroupItem, TableDefaultColumn } from '@/models/tableModels';

import { DataTableProps } from './types';

/*
 * COLUMNS
 *********************************************/
export function getColumnWidth<T>(
  column: TableDefaultColumn,
  widthMap = { [ColumnWidth.tiny]: 87, [ColumnWidth.small]: 137, [ColumnWidth.medium]: 310, [ColumnWidth.large]: 433 },
  overrides?: Partial<Record<keyof T, number>>,
) {
  // Column widths were calculated using a 14px font size
  // The widths were measured using N (upper range limit) of the widest character ('W')
  // - tiny: 0-7 characters
  // - small: 8-11 characters
  // - medium: 12-25 characters
  // - large: 26-35 characters

  const overwrittenValue = overrides?.[column.field as keyof T];

  if (overwrittenValue) {
    return overwrittenValue;
  }

  const valueFromMap = widthMap[column.width as unknown as keyof typeof widthMap];

  if (valueFromMap) {
    return valueFromMap;
  }

  if (column.width) {
    return Number(column.width);
  }

  return widthMap[ColumnWidth.small];
}

export function getTableColumnsDefs<T>(defaultColumns: TableDefaultColumn[], fields: Array<keyof T>) {
  return defaultColumns
    .filter((column) => fields.includes(column.field as keyof T))
    .sort(({ field: column1Field }, { field: column2Field }) => {
      const column1Index = fields.indexOf(column1Field as keyof T);
      const column2Index = fields.indexOf(column2Field as keyof T);

      return column1Index - column2Index;
    });
}

/*
 * PAGINATION
 *********************************************/
export const getPaginationFromQuery = (query: ParsedUrlQuery, defaultPagination: PaginationState = { pageIndex: 0, pageSize: 25 }) => {
  const page = Array.isArray(query.page) ? query.page[0] : query.page;
  const pageSize = Array.isArray(query.page_size) ? query.page_size[0] : query.page_size;

  return {
    pageIndex: page ? Number(page) - 1 : defaultPagination.pageIndex,
    pageSize: pageSize ? Number(pageSize) : defaultPagination.pageSize,
  };
};

export const getQueryFromPagination = (pagination: PaginationState) => {
  const query = new URLSearchParams();
  query.set('page', String(pagination.pageIndex + 1));
  query.set('page_size', String(pagination.pageSize));
  return query;
};

/*
 * SORTING
 *********************************************/
export const getSortingFromQuery = (query: ParsedUrlQuery, defaultSorting?: ColumnSort): SortingState => {
  const ordering = Array.isArray(query.ordering) ? query.ordering[0] : query.ordering;
  if (!ordering) return defaultSorting ? [defaultSorting] : [];
  return [{ id: ordering.replace('-', ''), desc: ordering.indexOf('-') === 0 } as ColumnSort];
};

export const getQueryFromSorting = (sorting: SortingState) => {
  const query = new URLSearchParams();
  const sortingColumn = sorting[0];
  query.set('ordering', sortingColumn ? `${sortingColumn.desc ? '-' : ''}${sortingColumn.id}` : '');
  return query;
};

/*
 * GLOBAL FILTER
 *********************************************/
export const getSearchTermFromQuery = (query: ParsedUrlQuery): string => {
  return (Array.isArray(query.q) ? query.q[0] : query.q) || '';
};

export const getQueryFromSearchTerm = (searchTerm: string) => {
  const query = new URLSearchParams();
  query.set('q', searchTerm);
  return query;
};

export const getFilterFromSearchTerm = (searchTerm: string, searchTextFields: string[] = []) => {
  return searchTerm
    ? [
        {
          condition: 'AND',
          filter_group: searchTextFields.map((field) => ({
            condition: 'OR',
            field,
            operator: 'icontains',
            value: searchTerm,
          })),
        },
      ]
    : [];
};

/*
 * TABLE FILTERS
 *********************************************/
const FILTER_CONDITION_START = '(';
const FILTER_CONDITION_END = ')';
const FILTER_CONDITION_SEPARATOR = ':';
const FILTER_NEGATED_PREFIX = '!';

const extractFilters = (query: string): string[][] => {
  // Regex to match the content inside the parentheses
  const regex = /\(([^)]+)\)/g;

  // Get all the matches
  const matches = [...query.matchAll(regex)];

  // Return the content inside the parentheses
  return matches.map((match) => match[1].split(FILTER_CONDITION_SEPARATOR));
};

export const parseFilterValue = (value: string, operator?: string) => {
  if (['isnull', '-isnull'].indexOf(operator || '') !== -1) return true;

  if (value === undefined) return undefined;

  if (value === 'true' || value === 'false') return value === 'true';

  if (typeof value === 'boolean') return value;

  if (!isNaN(Number(value))) return Number(value);

  return decodeURIComponent(value);
};

export const getFiltersFromQuery = (query: ParsedUrlQuery, filterKey: string = 'filters'): FilterConditionGroupItem[] => {
  const filters = Array.isArray(query[filterKey]) ? query[filterKey][0] : query[filterKey];
  const filtersExtracted = extractFilters(filters || '');

  return filtersExtracted.map(([field, operator, value, modifier]) => {
    const operatorParsed = operator ? operator.replace(FILTER_NEGATED_PREFIX, '') : undefined;
    const negated = operator?.indexOf('!') === 0;
    const valueParsed = parseFilterValue(value, operator);

    return {
      field,
      operator: operatorParsed,
      negated,
      value: valueParsed,
      modifier,
    } as FilterConditionGroupItem;
  });
};

export const getFilterConditionFromFilters = (filters: FilterConditionGroupItem[]) => {
  if (!filters.length) return [];
  return [
    {
      condition: 'AND',
      filter_group: filters,
    },
  ];
};

export const getQueryFromFilters = (filters: FilterConditionGroupItem[], filterKey: string = 'filters') => {
  const query = new URLSearchParams();

  const filtersQuery = filters.map((filter) => {
    const { field, negated, operator, value, modifier } = filter;
    const filterQuery = [field, operator ? `${negated ? FILTER_NEGATED_PREFIX : ''}${operator}` : ''];
    if (value !== undefined) {
      filterQuery.push(encodeURI(value));
    }
    if (modifier !== undefined) {
      filterQuery.push(modifier);
    }
    return `${FILTER_CONDITION_START}${filterQuery.join(FILTER_CONDITION_SEPARATOR)}${FILTER_CONDITION_END}`;
  });

  if (filtersQuery) {
    query.set(filterKey, filtersQuery.join(''));
  }
  return query;
};

export const getRowId = <TData extends RowData>(row: TData, rowId: DataTableProps<TData>['rowId']): string => {
  if (typeof rowId === 'function') {
    return rowId(row);
  }

  return String(row[rowId as keyof TData]);
};

export const readCookie = (name: string): string => {
  if (typeof document === 'undefined') return '';
  const m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return m ? decodeURIComponent(m.pop() as string) : '';
};
